/** @jsxImportSource @emotion/react */
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import * as authSelectors from "../../auth/authSelectors";
import { Drawer } from "../../../components/Drawer";
import { ButtonAction, ButtonPrimary } from "../../../components/NewButtons";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { StyledForm } from "../../../components/StyledForm";
import { SpinnerBlock } from "../../common/Spinner";
import { css } from "@emotion/react";
import {
  transformPriceInput,
  validateAboveZero,
} from "../../../components/forms/inputs/PriceInput";
import {
  CreateProductPayload,
  getClassificationDisplayValue,
  ProductClassification,
} from "../models/product";
import {
  useCreateProduct,
  useUploadProductImage,
} from "../service/products-api";
import { PrimaryButton } from "../../../components/Buttons";
import { colors } from "../../../components/colors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useState } from "react";
import { DrawerSubHeader } from "../../../components/DrawerSubHeader";
import { useGetVouchers, usePunchCards } from "../../../service/api";
import { getCountrySpecificValues } from "../../../util";
import { LabelledRadioGroupField } from "../../../components/forms/fields/LabelledRadioGroupField";
import { customRegister } from "../../../components/forms/inputs/util";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../appRoutingHelpers";
import { ProductInformationForm } from "./ProductInformationForm";
import { ProductPricingForm } from "./ProductPricingForm";
import { ProductImageForm } from "./ProductImageForm";
import { Divider } from "../../../components/Divider";
import { SelectCountryInput } from "../../../components/forms/inputs/SelectCountryInput";
import { useGetProductCategories } from "../../product-categories/service/product-categories-api";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const additionalFormStyles = css`
  gap: 16px;
  align-items: flex-start;
`;

const classificationsWrapperStyle = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  width: 100%;
`;

const buttonsWrapperStyle = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const validationSchema = yup.object({
  name: yup.string().required("Enter admin name to continue"),
  title: yup.string().required("Enter product name to continue"),
  priceAmount: validateAboveZero("priceCurrency").required(
    "Enter price to continue"
  ),
  priceCurrency: yup.string().required("Choose currency to continue"),
  classification: yup.string().required("Choose classification to continue"),
  productCategoryId: yup
    .string()
    .test(
      "testing product category required",
      "Choose product category to continue",
      (val, ctx) => {
        if (ctx.parent.classification === ProductClassification.Addition) {
          return true;
        }
        return !!val;
      }
    ),
  countryCode: yup.string().required("Choose country to continue"),
  voucherTypeId: yup.string().when("classification", {
    is: "voucher_type",
    then: (schema) => schema.required("Choose voucher to continue"),
    otherwise: (schema) => schema.optional(),
  }),
  productPackId: yup.string().when("classification", {
    is: "product_pack",
    then: (schema) => schema.required("Choose punch card to continue"),
    otherwise: (schema) => schema.optional(),
  }),
  files: yup.array().optional(),
  productCoolDownRulesetId: yup.string().optional(),
});

export const CreateProductDrawer = ({ isOpen, onClose }: Props) => {
  const { countryCode, currency, vatPercentage } = getCountrySpecificValues();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [
    isLoadingCreateAndAddAnother,
    setIsLoadingCreateAndAddAnother,
  ] = useState(false);
  const [isLoadingCreate, setIsLoadingCreate] = useState(false);
  const [advancedProductTypesOpen, setAdvancedProductTypesOpen] = useState(
    false
  );
  const navigate = useNavigate();
  const methods = useForm<CreateProductPayload & { files: File[] }>({
    defaultValues: {
      priceCurrency: currency,
      isPriceDifferentiationEnabled: false,
      countryCode,
      vat: vatPercentage,
      classification: "generic",
      imageUrl: "",
      files: [],
      labels: [],
      isEligibleForDiscount: true,
      productCoolDownRulesetId: "",
      productPackId: "",
      voucherTypeId: "",
      description: "",
      name: "",
      tags: [],
      title: "",
      priceAmount: 0,
      priceDeliveryAmount: 0,
    },
    resolver: yupResolver(validationSchema),
  });

  const {
    data: productCategories,
    isLoading: isLoadingProductCategories,
    error: productCategoriesError,
  } = useGetProductCategories(organizationKeyName);

  const {
    data: voucherTypesList,
    isLoading: isLoadingVoucherTypeList,
    error: voucherTypesListError,
  } = useGetVouchers(organizationKeyName);
  const {
    data: punchCards,
    isLoading: punchCardsLoading,
    error: punchCardsError,
  } = usePunchCards(organizationKeyName);

  const handleOnClose = () => {
    methods.reset();
    onClose();
  };

  const { mutateAsync: createProduct } = useCreateProduct();

  const { mutateAsync: uploadProductImage } = useUploadProductImage();

  const { ref: classificationRef, ...restClassificationField } = customRegister<
    CreateProductPayload & { files: File[] }
  >("classification", methods.register);

  const productCategoriesOptions = (productCategories || [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((o) => ({
      label: o.name,
      value: o.id,
    }));

  productCategoriesOptions.unshift({
    label: "None",
    value: "",
  });

  const voucherOptions = (voucherTypesList || [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((x) => ({
      label: x.name,
      value: x.id,
    }));

  voucherOptions.unshift({
    label: "None",
    value: "",
  });

  const punchCardsOptions = (punchCards || []).map((x: any) => ({
    label: x.name,
    value: x.id,
  }));

  punchCardsOptions.unshift({
    label: "None",
    value: "",
  });

  const onSubmit = async (
    data: CreateProductPayload & { files: File[] },
    type: "save_and_create_new" | "save_and_edit"
  ) => {
    if (type === "save_and_create_new") {
      setIsLoadingCreateAndAddAnother(true);
    } else {
      setIsLoadingCreate(true);
    }
    const { files, ...productData } = data;
    const payload = {
      ...productData,
    };
    try {
      payload.priceAmount = transformPriceInput(
        payload.priceAmount,
        payload.priceCurrency
      );
      if (!payload.productCoolDownRulesetId) {
        payload.productCoolDownRulesetId = null;
      }
      if (!payload.productPackId) {
        payload.productPackId = null;
      }
      if (!payload.voucherTypeId) {
        payload.voucherTypeId = null;
      }
      if (payload.isPriceDifferentiationEnabled) {
        payload.priceDeliveryAmount = transformPriceInput(
          payload.priceDeliveryAmount,
          payload.priceCurrency
        );
        payload.priceDeliveryCurrency = payload.priceCurrency;
      } else {
        payload.priceDeliveryAmount = "";
        payload.priceDeliveryCurrency = "";
      }
      const id = await createProduct({ data: payload, organizationKeyName });
      if (files?.length > 0) {
        await uploadProductImage({
          organizationKeyName,
          id,
          file: files[0],
        });
      }
      if (type === "save_and_create_new") {
        methods.reset();
      } else {
        navigate(routes.productEdit(organizationKeyName, id));
        handleOnClose();
      }
      if (type === "save_and_create_new") {
        setIsLoadingCreateAndAddAnother(false);
      } else {
        setIsLoadingCreate(false);
      }

      toastr.success("Success", `Product successfully created`);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  useHandleFetchError(productCategoriesError);
  useHandleFetchError(voucherTypesListError);
  useHandleFetchError(punchCardsError);

  return (
    <Drawer
      title="Create product"
      width={700}
      buttons={
        <div css={buttonsWrapperStyle}>
          <ButtonPrimary
            onClick={methods.handleSubmit(async (data) => {
              await onSubmit(data, "save_and_create_new");
            })}
            disabled={isLoadingCreateAndAddAnother}
          >
            {isLoadingCreateAndAddAnother ? (
              <ActionSpinner />
            ) : (
              "Create and add another"
            )}
          </ButtonPrimary>
          <ButtonAction
            onClick={methods.handleSubmit(async (data) => {
              await onSubmit(data, "save_and_edit");
            })}
            disabled={isLoadingCreate}
            type="button"
          >
            {isLoadingCreate ? <ActionSpinner /> : "Create"}
          </ButtonAction>
        </div>
      }
      open={isOpen}
      onClose={handleOnClose}
    >
      <FormProvider {...methods}>
        {isLoadingProductCategories ||
        isLoadingVoucherTypeList ||
        punchCardsLoading ? (
          <SpinnerBlock />
        ) : (
          <StyledForm additionalStyles={additionalFormStyles}>
            <DrawerSubHeader header="Product type" />
            <div css={classificationsWrapperStyle}>
              <LabelledRadioGroupField
                innerRef={classificationRef}
                options={[
                  {
                    label: getClassificationDisplayValue(
                      ProductClassification.Generic
                    ),
                    value: ProductClassification.Generic,
                    description: "Product sold to customers. E.g. burger.",
                  },
                  {
                    label: getClassificationDisplayValue(
                      ProductClassification.Addition
                    ),
                    value: ProductClassification.Addition,
                    description:
                      "Addition to a main product. E.g. extra cheese.",
                  },
                ]}
                {...restClassificationField}
              />
            </div>
            <PrimaryButton
              buttonSize="small"
              backgroundColor={colors.transparent}
              color={colors.textPrimary}
              type="button"
              hoverColor={colors.whiteHover}
              onClick={() =>
                setAdvancedProductTypesOpen(!advancedProductTypesOpen)
              }
              icon={
                advancedProductTypesOpen ? (
                  <FontAwesomeIcon icon={solid("chevron-up")} />
                ) : (
                  <FontAwesomeIcon icon={solid("chevron-down")} />
                )
              }
            >
              {advancedProductTypesOpen
                ? "Hide advanced product types"
                : "Show advanced product types"}
            </PrimaryButton>
            {advancedProductTypesOpen && (
              <div css={classificationsWrapperStyle}>
                <LabelledRadioGroupField
                  innerRef={classificationRef}
                  options={[
                    {
                      label: getClassificationDisplayValue(
                        ProductClassification.VoucherStack
                      ),
                      value: ProductClassification.VoucherStack,
                      description:
                        "A stack of vouchers that can be sold together",
                    },
                    {
                      label: getClassificationDisplayValue(
                        ProductClassification.VoucherOffer
                      ),
                      value: ProductClassification.VoucherOffer,
                      description: "A single voucher offer",
                    },
                    {
                      label: getClassificationDisplayValue(
                        ProductClassification.Delivery
                      ),
                      value: ProductClassification.Delivery,
                      description: "Used for delivery orders",
                    },
                    {
                      label: getClassificationDisplayValue(
                        ProductClassification.DeliveryBuffer
                      ),
                      value: ProductClassification.DeliveryBuffer,
                      description:
                        "Used when cart total is above a minimum delivery order total",
                    },
                  ]}
                  {...restClassificationField}
                />
              </div>
            )}
            <Divider />
            <DrawerSubHeader
              header="Product information"
              subtext="Basic product information"
            />
            <ProductInformationForm />
            <SelectCountryInput name="countryCode" />
            <Divider />
            <DrawerSubHeader header="Product pricing" />
            <ProductPricingForm />
            <Divider />
            <DrawerSubHeader header="Product image" />
            <ProductImageForm />
          </StyledForm>
        )}
      </FormProvider>
    </Drawer>
  );
};
